import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, Users, Target, Eye, Globe, Car, School, ShoppingCart, Utensils, Hospital } from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { Link } from 'react-router-dom';

const About = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [showMore, setShowMore] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll('[data-animate]');

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach(observer => observer.disconnect());
    }, []);

    const teamStats = [
        { number: "12+", label: "Team Members" },
        { number: "10+", label: "Successful Projects" },
        { number: "3+", label: "Industry Domains" },
        { number: "99%", label: "Client Satisfaction" }
    ];

    const teamMembers = [
        { name: 'Asfak Sherasiya', role: 'CEO & Founder', image: 'team1.jpg' },
        { name: 'Jaim Sherasiya', role: 'Lead Developer', image: 'team2.jpg' },
        { name: 'Shejad Kadivar', role: 'AI Specialist', image: 'team3.jpg' },
        { name: 'Avesh Vakaliya', role: 'Product Designer', image: 'team4.jpg' },
        { name: 'Sakil Parasara', role: 'Platform Engineer', image: 'team5.jpg' },
        { name: 'Gulammustufa Maraviya', role: 'Support Lead', image: 'team6.jpg' },
        { name: 'Mubin Sherasiya', role: 'Support Lead', image: 'team6.jpg' },
        { name: 'Ayaz Sherasiya', role: 'Support Lead', image: 'team6.jpg' },
        { name: 'Another Member', role: 'Support Lead', image: 'team6.jpg' },
    ];

    const domains = [
        { name: "Healthcare", desc: "Hospital & clinic management with AI-powered solutions.", icon: <Hospital className='w-10 h-10' /> },
        { name: "Restaurants", desc: "Smart RMS platforms for F&B businesses.", icon: <Utensils className='w-10 h-10' /> },
        { name: "E-Commerce", desc: "Custom online stores & marketplaces.", icon: <ShoppingCart className='w-10 h-10' /> },
        { name: "Education", desc: "Interactive e-learning platforms.", icon: <School className='w-10 h-10' /> },
        { name: "Automobile", desc: "Vehicle Bazaar and automotive tech solutions.", icon: <Car className='w-10 h-10' /> },
        { name: "Corporate", desc: "CRM, HRMS, and enterprise websites.", icon: <Globe className='w-10 h-10' /> },
    ];

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar />

            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto text-center">
                    <div
                        className="animate-fade-in-up"
                        style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                    >
                        <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                            About
                            <br />
                            <span className="text-green-500 inline-block animate-pulse">OneHexa Tech</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
                            Empowering businesses with <span className="text-green-500 font-semibold">innovative software solutions</span> that transform industries and accelerate growth.
                        </p>
                    </div>

                    {/* Scroll Indicator */}
                    <div className="animate-bounce mt-16">
                        <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gray-900" data-animate>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {teamStats.map((stat, index) => (
                            <div key={index} className="text-white">
                                <div className="text-4xl md:text-5xl font-bold mb-2 text-green-400 animate-pulse">{stat.number}</div>
                                <div className="text-gray-300 text-lg">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Us Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            About <span className="text-green-500">Us</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize">
                            Founded in 2024, <span className="text-green-500 font-semibold">OneHexa Tech</span> is a product-based software company located in Wankaner, Morbi, Gujarat.
                            We specialize in delivering scalable and innovative solutions like <strong>HIMS, RMS, CRM, HRMS, Pocket Pro, Vehicle Bazaar, E-Commerce, Kids Learning,</strong> and modern business websites.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Our Mission */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Target className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Mission</h3>
                            <p className="text-gray-600 leading-relaxed">
                                To empower businesses by delivering <span className="text-green-500 font-semibold">custom, reliable, and future-ready software solutions</span> that enhance efficiency, accelerate digital transformation, and create lasting value.
                            </p>
                        </div>

                        {/* Our Vision */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Eye className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Vision</h3>
                            <p className="text-gray-600 leading-relaxed">
                                To become a <span className="text-green-500 font-semibold">globally trusted technology partner</span>, leading innovation in software products and solutions that reshape industries and make businesses smarter.
                            </p>
                        </div>

                        {/* Our Values */}
                        <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
                            <Users className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
                            <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Values</h3>
                            <ul className="list-disc list-inside text-gray-600 space-y-2">
                                <li>Innovation with purpose</li>
                                <li>Integrity and transparency</li>
                                <li>Client-first collaboration</li>
                                <li>Commitment to excellence</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Domain Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-animate>
                <div className={`max-w-7xl mx-auto text-center transition-all duration-1000 delay-250 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                        Our <span className="text-green-500">Domains</span>
                    </h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
                        We work across multiple industries to deliver <span className="text-green-500 font-semibold">cutting-edge digital products</span>.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {domains.map((domain, idx) => (
                            <div key={idx} className="bg-gray-50 p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border hover:border-green-300">
                                <div className="w-10 h-10 text-green-500 mx-auto mb-4">
                                    {domain.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{domain.name}</h3>
                                <p className="text-gray-600">{domain.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team Section with Show More */}
            <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Meet Our <span className="text-green-500">Team</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize">
                            The people powering <span className="text-green-500 font-semibold">OneHexa Tech</span>.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {(showMore ? teamMembers : teamMembers.slice(0, 6)).map((member, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200"
                            >
                                <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`/${member.image}`} alt={member.name} className="w-full h-full object-cover" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-green-600 transition-colors duration-300">{member.name}</h3>
                                <p className="text-sm text-gray-600">{member.role}</p>
                            </div>
                        ))}
                    </div>

                    {/* Show More Button */}
                    <div className="text-center mt-8">
                        <button
                            onClick={() => setShowMore(!showMore)}
                            className="px-6 py-3 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition-all"
                        >
                            {showMore ? "Show Less" : "Show More"}
                        </button>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Grow with <span className="text-green-400">OneHexa Tech?</span>
                    </h2>
                    <p className="text-xl text-gray-300 mb-8">
                        Let's build smarter solutions together — from healthcare to e-commerce and beyond.
                    </p>
                    <Link to="/contact">
                        <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                            Contact Us
                        </button>
                    </Link>
                </div>
            </section>

            <Footer />

            <style jsx>{`
                @keyframes fade-in-up {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fade-in-up {
                    animation: fade-in-up 1s ease-out;
                }
            `}</style>
        </div>
    );
};

export default About;




// import React, { useState, useEffect } from 'react';
// import { ChevronDown, ArrowRight, Users, Target, Eye, Globe, Car, School, ShoppingCart, Utensils, Hospital } from 'lucide-react';
// import Navbar from '../layout/Navbar';
// import Footer from '../layout/Footer';

// const About = () => {
//     const [scrollY, setScrollY] = useState(0);
//     const [isVisible, setIsVisible] = useState({});
//     const [showMore, setShowMore] = useState(false);

//     useEffect(() => {
//         const handleScroll = () => setScrollY(window.scrollY);
//         window.addEventListener('scroll', handleScroll);
//         return () => window.removeEventListener('scroll', handleScroll);
//     }, []);

//     useEffect(() => {
//         const observers = new Map();
//         const elements = document.querySelectorAll('[data-animate]');

//         elements.forEach((el, index) => {
//             const observer = new IntersectionObserver(
//                 ([entry]) => {
//                     if (entry.isIntersecting) {
//                         setIsVisible(prev => ({ ...prev, [index]: true }));
//                     }
//                 },
//                 { threshold: 0.1 }
//             );
//             observer.observe(el);
//             observers.set(index, observer);
//         });

//         return () => observers.forEach(observer => observer.disconnect());
//     }, []);

//     const teamStats = [
//         { number: "12+", label: "Team Members" },
//         { number: "10+", label: "Successful Projects" },
//         { number: "3+", label: "Industry Domains" },
//         { number: "99%", label: "Client Satisfaction" }
//     ];

//     const teamMembers = [
//         { name: 'Asfak Sherasiya', role: 'CEO & Founder', image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop' },
//         { name: 'Jaim Sherasiya', role: 'Lead Developer', image: 'https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=400&h=400&fit=crop' },
//         { name: 'Shejad Kadivar', role: 'AI Specialist', image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop' },
//         { name: 'Avesh Vakaliya', role: 'Product Designer', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop' },
//         { name: 'Sakil Parasara', role: 'Platform Engineer', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop' },
//         { name: 'Gulammustufa Maraviya', role: 'Support Lead', image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop' },
//         { name: 'Mubin Sherasiya', role: 'Support Lead', image: 'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=400&h=400&fit=crop' },
//         { name: 'Ayaz Sherasiya', role: 'Support Lead', image: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&h=400&fit=crop' },
//         { name: 'Another Member', role: 'Support Lead', image: 'https://images.unsplash.com/photo-1504593811423-6dd665756598?w=400&h=400&fit=crop' },
//     ];

//     const domains = [
//         { name: "Healthcare", desc: "Hospital & clinic management with AI-powered solutions.", icon: <Hospital className='w-10 h-10' />, img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop" },
//         { name: "Restaurants", desc: "Smart RMS platforms for F&B businesses.", icon: <Utensils className='w-10 h-10' />, img: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=300&fit=crop" },
//         { name: "E-Commerce", desc: "Custom online stores & marketplaces.", icon: <ShoppingCart className='w-10 h-10' />, img: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=400&h=300&fit=crop" },
//         { name: "Education", desc: "Interactive e-learning platforms.", icon: <School className='w-10 h-10' />, img: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop" },
//         { name: "Automobile", desc: "Vehicle Bazaar and automotive tech solutions.", icon: <Car className='w-10 h-10' />, img: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400&h=300&fit=crop" },
//         { name: "Corporate", desc: "CRM, HRMS, and enterprise websites.", icon: <Globe className='w-10 h-10' />, img: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop" },
//     ];

//     return (
//         <div className="min-h-screen bg-white text-gray-800">
//             <Navbar />

//             {/* Hero Section with Background Image */}
//             <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
//                 <div className="absolute inset-0 z-0">
//                     <img 
//                         src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920&h=1080&fit=crop" 
//                         alt="Team Collaboration" 
//                         className="w-full h-full object-cover"
//                     />
//                     <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/10 to-white"></div>
//                 </div>

//                 <div className="relative z-10 max-w-7xl mx-auto text-center">
//                     <div
//                         className="animate-fade-in-up"
//                         style={{ transform: `translateY(${scrollY * 0.1}px)` }}
//                     >
//                         <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
//                             About
//                             <br />
//                             <span className="text-green-500 inline-block animate-pulse">OneHexa Tech</span>
//                         </h1>
//                         <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
//                             Empowering businesses with <span className="text-green-500 font-semibold">innovative software solutions</span> that transform industries and accelerate growth.
//                         </p>
//                     </div>

//                     {/* Scroll Indicator */}
//                     <div className="animate-bounce mt-16">
//                         <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
//                     </div>
//                 </div>
//             </section>

//             {/* Stats Section with Tech Background */}
//             <section className="relative py-16 overflow-hidden" data-animate>
//                 <div className="absolute inset-0 z-0">
//                     <img 
//                         src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=600&fit=crop" 
//                         alt="Technology Network" 
//                         className="w-full h-full object-cover"
//                     />
//                     <div className="absolute inset-0 bg-gray-900/90"></div>
//                 </div>

//                 <div className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
//                         {teamStats.map((stat, index) => (
//                             <div key={index} className="text-white">
//                                 <div className="text-4xl md:text-5xl font-bold mb-2 text-green-400 animate-pulse">{stat.number}</div>
//                                 <div className="text-gray-300 text-lg">{stat.label}</div>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </section>

//             {/* About Us Section */}
//             <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
//                 <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
//                         <div>
//                             <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//                                 About <span className="text-green-500">Us</span>
//                             </h2>
//                             <p className="text-xl text-gray-600 capitalize leading-relaxed">
//                                 Founded in 2024, <span className="text-green-500 font-semibold">OneHexa Tech</span> is a product-based software company located in Wankaner, Morbi, Gujarat.
//                                 We specialize in delivering scalable and innovative solutions like <strong>HIMS, RMS, CRM, HRMS, Pocket Pro, Vehicle Bazaar, E-Commerce, Kids Learning,</strong> and modern business websites.
//                             </p>
//                         </div>
//                         <div className="relative">
//                             <div className="absolute inset-0 bg-green-500/20 rounded-2xl blur-2xl"></div>
//                             <img 
//                                 src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&h=600&fit=crop" 
//                                 alt="Modern Office" 
//                                 className="relative rounded-2xl shadow-2xl w-full h-auto"
//                             />
//                         </div>
//                     </div>

//                     <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
//                         {/* Our Mission */}
//                         <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
//                             <Target className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
//                             <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Mission</h3>
//                             <p className="text-gray-600 leading-relaxed">
//                                 To empower businesses by delivering <span className="text-green-500 font-semibold">custom, reliable, and future-ready software solutions</span> that enhance efficiency, accelerate digital transformation, and create lasting value.
//                             </p>
//                         </div>

//                         {/* Our Vision */}
//                         <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
//                             <Eye className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
//                             <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Vision</h3>
//                             <p className="text-gray-600 leading-relaxed">
//                                 To become a <span className="text-green-500 font-semibold">globally trusted technology partner</span>, leading innovation in software products and solutions that reshape industries and make businesses smarter.
//                             </p>
//                         </div>

//                         {/* Our Values */}
//                         <div className="bg-white rounded-2xl p-8 shadow-lg border hover:shadow-xl transition-all duration-300 group hover:border-green-200">
//                             <Users className="w-10 h-10 text-gray-500 mb-4 group-hover:text-green-500 transition-colors" />
//                             <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-green-600">Our Values</h3>
//                             <ul className="list-disc list-inside text-gray-600 space-y-2">
//                                 <li>Innovation with purpose</li>
//                                 <li>Integrity and transparency</li>
//                                 <li>Client-first collaboration</li>
//                                 <li>Commitment to excellence</li>
//                             </ul>
//                         </div>
//                     </div>
//                 </div>
//             </section>

//             {/* Domain Section */}
//             <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-animate>
//                 <div className={`max-w-7xl mx-auto text-center transition-all duration-1000 delay-250 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//                         Our <span className="text-green-500">Domains</span>
//                     </h2>
//                     <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
//                         We work across multiple industries to deliver <span className="text-green-500 font-semibold">cutting-edge digital products</span>.
//                     </p>
//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//                         {domains.map((domain, idx) => (
//                             <div key={idx} className="bg-gray-50 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border hover:border-green-300 overflow-hidden group">
//                                 <div className="relative h-48 overflow-hidden">
//                                     <img 
//                                         src={domain.img} 
//                                         alt={domain.name}
//                                         className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
//                                     />
//                                     <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
//                                 </div>
//                                 <div className="p-6">
//                                     <div className="w-10 h-10 text-green-500 mb-4">
//                                         {domain.icon}
//                                     </div>
//                                     <h3 className="text-xl font-bold text-gray-800 mb-2">{domain.name}</h3>
//                                     <p className="text-gray-600">{domain.desc}</p>
//                                 </div>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </section>

//             {/* Team Section with Show More */}
//             <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8" data-animate>
//                 <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <div className="text-center mb-16">
//                         <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//                             Meet Our <span className="text-green-500">Team</span>
//                         </h2>
//                         <p className="text-xl text-gray-600 max-w-3xl mx-auto capitalize">
//                             The people powering <span className="text-green-500 font-semibold">OneHexa Tech</span>.
//                         </p>
//                     </div>

//                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//                         {(showMore ? teamMembers : teamMembers.slice(0, 6)).map((member, index) => (
//                             <div
//                                 key={index}
//                                 className="bg-white rounded-2xl p-6 text-center hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200"
//                             >
//                                 <div className="w-32 h-32 mx-auto mb-4 rounded-full bg-gray-200 overflow-hidden relative">
//                                     <div className="absolute inset-0 bg-green-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
//                                     <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
//                                 </div>
//                                 <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-green-600 transition-colors duration-300">{member.name}</h3>
//                                 <p className="text-sm text-gray-600">{member.role}</p>
//                             </div>
//                         ))}
//                     </div>

//                     {/* Show More Button */}
//                     <div className="text-center mt-8">
//                         <button
//                             onClick={() => setShowMore(!showMore)}
//                             className="px-6 py-3 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition-all"
//                         >
//                             {showMore ? "Show Less" : "Show More"}
//                         </button>
//                     </div>
//                 </div>
//             </section>

//             {/* CTA Section with Background */}
//             <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-animate>
//                 <div className="absolute inset-0 z-0">
//                     <img 
//                         src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1920&h=600&fit=crop" 
//                         alt="Modern Workspace" 
//                         className="w-full h-full object-cover"
//                     />
//                     <div className="absolute inset-0 bg-gray-900/85"></div>
//                 </div>

//                 <div className={`relative z-10 max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[4] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
//                         Ready to Grow with <span className="text-green-400">OneHexa Tech?</span>
//                     </h2>
//                     <p className="text-xl text-gray-300 mb-8">
//                         Let's build smarter solutions together — from healthcare to e-commerce and beyond.
//                     </p>
//                     <a href="/contact">
//                         <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
//                             Contact Us
//                         </button>
//                     </a>
//                 </div>
//             </section>

//             <Footer />

//             <style jsx>{`
//                 @keyframes fade-in-up {
//                     from {
//                         opacity: 0;
//                         transform: translateY(30px);
//                     }
//                     to {
//                         opacity: 1;
//                         transform: translateY(0);
//                     }
//                 }
//                 .animate-fade-in-up {
//                     animation: fade-in-up 1s ease-out;
//                 }
//             `}</style>
//         </div>
//     );
// };

// export default About;